import { PERSON_CONTACT_AUTOFILL_QUERY, SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { Controller } from "react-hook-form";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import { People } from "@/pages/_layouts/jobs/types";
import AvatarDetails from "@/components/AvatarDetails";
import { useState, useEffect, useCallback } from "react";
import Divider from "@/components/Divider";
import TextField from "@/components/TextField";
import useCurrentUser from "@/hooks/useCurrentUser";
import { NumberFormatBase } from "react-number-format";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";

interface EmployerData extends People {
  name?: string;
  __typename?: string;
}

interface ContactInformationStepProps {
  form: any;
  initialEmployerLoad?: boolean;
}

export function ContactInformationStep({ form }: ContactInformationStepProps) {
  const [employerSearchTerm, setEmployerSearchTerm] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const { user } = useCurrentUser();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });

  const {
    control,
    clearErrors,
    trigger,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = form;

  const storedEmployerData = watch("employerData");
  const serviceLevel = watch("serviceLevel");
  const isAdvancedOrExecutive = serviceLevel === "Advanced" || serviceLevel === "Executive";
  const [selectedEmployer, setSelectedEmployer] = useState<EmployerData | null>(storedEmployerData);

  const getEmployerName = useCallback((employer: EmployerData | null) => {
    if (!employer) return "";
    return employer.name || `${employer.firstName || ""} ${employer.lastName || ""}`.trim();
  }, []);

  const isPointOfContactEmpty = useCallback((pointOfContact: any): boolean => {
    if (!pointOfContact) return true;

    const isEmpty = (value: string | undefined | null) => !value || value.trim() === "";

    return isEmpty(pointOfContact.name) && isEmpty(pointOfContact.email) && isEmpty(pointOfContact.title);
  }, []);

  const populatePointOfContactIfEmpty = useCallback(
    (employer: EmployerData) => {
      const pointOfContact = watch("pointOfContact") || {};

      if (isPointOfContactEmpty(pointOfContact)) {
        if (isAdvancedOrExecutive && user) {
          // For Advanced/Executive, use Career Consultant info
          setValue("pointOfContact", {
            name: user.name || "",
            email: user.email || "",
            phone: employer.phoneNumbers?.[0]?.phoneNumber || "",
            title: "Career Consultant"
          });
        } else {
          // For Assisted, use employer info
          const employmentTitle = employer.employments?.filter(
            (employment: { isPrimary: boolean }) => employment.isPrimary
          )?.[0]?.title;

          setValue("pointOfContact", {
            name: getEmployerName(employer),
            email: employer.email || "",
            title: employmentTitle || ""
          });
        }

        const fields = ["pointOfContact.name", "pointOfContact.email", "pointOfContact.title"];
        if (errors?.pointOfContact) {
          clearErrors(fields);
        }
        trigger(fields);
      }
    },
    [watch, isPointOfContactEmpty, setValue, errors, clearErrors, trigger, getEmployerName, isAdvancedOrExecutive, user]
  );

  const { data: employerData, loading: employerLoading } = useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: { search: employerSearchTerm || "" },
      first: 10
    },
    skip: employerSearchTerm.length < 2 && !isUserTyping // Only skip if search term is too short and user isn't typing
  });

  const selectedPgaId = selectedEmployer?.pgaId;
  const { data: contactAutofillData } = useQuery(PERSON_CONTACT_AUTOFILL_QUERY, {
    variables: { pgaId: selectedPgaId ?? "" },
    skip: !selectedPgaId || !canViewPersonContact,
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (!storedEmployerData || !initialLoad) return;

    let name = "";
    if (storedEmployerData.__typename === "JobPostEmployer") {
      name = storedEmployerData.name || "";
    } else {
      name = getEmployerName(storedEmployerData);
    }

    setEmployerSearchTerm(name);
    setSelectedEmployer(storedEmployerData);

    // If we have an ID, set it directly
    if (storedEmployerData.id) {
      setValue("employerId", storedEmployerData.id);
      if (errors.employerId) {
        clearErrors("employerId");
      }
    }

    setInitialLoad(false);
  }, [storedEmployerData, initialLoad, getEmployerName, setValue, clearErrors, errors.employerId]);

  // Prefill contact information for Advanced/Executive when component mounts
  useEffect(() => {
    if (isAdvancedOrExecutive && user && isPointOfContactEmpty(watch("pointOfContact"))) {
      setValue("pointOfContact", {
        name: user.name || "",
        email: user.email || "",
        title: "Career Consultant"
      });
    }
  }, [isAdvancedOrExecutive, user, isPointOfContactEmpty, watch, setValue]);

  useEffect(() => {
    const contact = contactAutofillData?.person;
    if (!contact) return;

    const pointOfContact = getValues("pointOfContact") || {};
    if (!pointOfContact.email && contact.email) {
      setValue("pointOfContact.email", contact.email);
    }
    const phone = contact.phoneNumbers?.[0]?.phoneNumber;
    if (!pointOfContact.phone && phone) {
      setValue("pointOfContact.phone", phone);
    }
  }, [contactAutofillData, getValues, setValue]);

  const handleEmployerSelect = (employer: EmployerData, field: any) => {
    field.onChange(employer.id);
    setValue("employerData", employer);
    setSelectedEmployer(employer);
    const name = getEmployerName(employer);
    setEmployerSearchTerm(name);
    setIsUserTyping(false);
    populatePointOfContactIfEmpty(employer);
    if (errors.employerId) {
      clearErrors("employerId");
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter contact information for the job.</div>

      <div className="col-span-full">
        <p className="text-title-medium mb-2">Employer</p>
        <p className="text-body-medium mb-6">
          {isAdvancedOrExecutive
            ? `Employer will not receive email notifications because this is an ${serviceLevel} posting.`
            : "The employer is the person who will receive notifications when people apply to this position or other status changes occur (e.g. a job has been published)."}
        </p>
        <Controller
          name="employerId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              label="Employer"
              query={(() => {
                if (isUserTyping) return employerSearchTerm;
                if (selectedEmployer) return getEmployerName(selectedEmployer);
                return employerSearchTerm;
              })()}
              loadingResults={employerLoading}
              data={employerData?.people?.nodes || []}
              onChangeText={(string) => {
                setIsUserTyping(true);
                setEmployerSearchTerm(string);

                // If the input is being cleared, reset the employer
                if (!string || string.trim() === "") {
                  setSelectedEmployer(null);
                  field.onChange("");
                  setValue("employerData", null);
                  return;
                }

                if (selectedEmployer) {
                  const currentName = getEmployerName(selectedEmployer);

                  if (
                    string &&
                    currentName &&
                    !currentName.toLowerCase().includes(string.toLowerCase()) &&
                    !string.toLowerCase().includes(currentName.toLowerCase())
                  ) {
                    setSelectedEmployer(null);
                    field.onChange("");
                  }
                }
              }}
              reset={() => {
                field.onChange("");
                setValue("employerData", null);
                setSelectedEmployer(null);
                setEmployerSearchTerm("");
                setIsUserTyping(false);
              }}
              onSelect={(value: EmployerData) => handleEmployerSelect(value, field)}
              renderItem={(data: EmployerData) => (
                <AvatarDetails
                  title={getEmployerName(data)}
                  text={data.email}
                  picture={data.profilePhoto}
                  icon="person"
                />
              )}
              error={!!errors.employerId}
              errorText={errors.employerId?.message}
            />
          )}
        />
      </div>

      <div className="py-3">
        <Divider />
      </div>

      <div className="col-span-full">
        <p className="text-title-medium mb-2">Job Contact</p>
        <p className="text-body-medium mb-6">
          The job contact is the person whose information will be listed on the job post.
        </p>

        <div className="col-span-full space-y-6">
          <Controller
            name="pointOfContact.name"
            control={control}
            rules={{ required: "Contact name is required" }}
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Contact Name"
                placeholder="Enter contact name"
                className="w-full"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  if ((e.target as HTMLInputElement).value && errors?.pointOfContact?.name) {
                    clearErrors("pointOfContact.name");
                  }
                  if (!(e.target as HTMLInputElement).value) {
                    trigger("pointOfContact.name");
                  }
                }}
                required
                error={!!errors?.pointOfContact?.name}
                errorText={errors?.pointOfContact?.name?.message as string}
              />
            )}
          />
          <Controller
            name="pointOfContact.title"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Contact Title"
                placeholder="Enter contact title"
                className="w-full"
                value={value}
                onChange={onChange}
              />
            )}
          />
          <Controller
            name="pointOfContact.email"
            control={control}
            rules={{ required: "Contact email is required" }}
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Contact Email"
                placeholder="Enter contact email"
                className="w-full"
                value={value}
                onChange={(e) => {
                  onChange(e);
                  if ((e.target as HTMLInputElement).value && errors?.pointOfContact?.email) {
                    clearErrors("pointOfContact.email");
                  }
                  if (!(e.target as HTMLInputElement).value) {
                    trigger("pointOfContact.email");
                  }
                }}
                required
                error={!!errors?.pointOfContact?.email}
                errorText={errors?.pointOfContact?.email?.message as string}
              />
            )}
          />
          <Controller
            name="pointOfContact.phone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <NumberFormatBase
                placeholder="Enter contact phone"
                className="w-full"
                customInput={TextField}
                value={value}
                onChange={(e) => {
                  onChange(e);
                  if ((e.target as HTMLInputElement).value && errors?.pointOfContact?.phone) {
                    clearErrors("pointOfContact.phone");
                  }
                  if (!(e.target as HTMLInputElement).value) {
                    trigger("pointOfContact.phone");
                  }
                }}
                label="Contact Phone"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
